import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import "../account.css";
import "./accountReferral.css";
import { CombinedContext } from "../../../context/combinedContext";
import billingRequest from "../../../request/billing";
import translations from "../../../translations/translations";
import {
	AlertCircle,
	ArrowDownLeft,
	ArrowUpRight,
	Check,
	CheckCircle,
	Clock,
	Copy,
	Gift,
	HelpCircle,
	Share2,
	Users,
} from "lucide-react";
import AccountReferralSkeleton from "./accountReferralSkeleton/accountReferralSkeleton";

const REFERRAL_VALUE = 15;
const REFERRAL_AMOUNT = "$15 USD";

const STATUS_TONE = {
	reward_issued: "ok",
	active: "ok",
	pending: "warn",
	pending_billing: "warn",
	pending_referrer_payment: "warn",
	canceled: "danger",
	cancelled: "danger",
};

const toneOf = (status) => STATUS_TONE[status] || "neutral";

const AccountReferral = () => {
	const { language, userData } = useContext(CombinedContext);
	const navigate = useNavigate();

	const [referral, setReferral] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [copied, setCopied] = useState(null);

	const t = translations[language].account.referral;

	const role = userData?.user?.role;
	const isOwner = role === "owner";

	useEffect(() => {
		if (!userData?.user?.userId) return;
		loadReferral();
	}, [userData]);

	const loadReferral = async () => {
		try {
			const res = await billingRequest.getReferralDashboard(userData.user.userId, userData.cognitoTokens.AccessToken);
			setReferral(res);
		} catch (err) {
			console.error("Failed to load referral dashboard:", err);
			setError(true);
		} finally {
			setLoading(false);
		}
	};

	const copyToClipboard = async (text, type) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopied(type);
			setTimeout(() => setCopied((prev) => (prev === type ? null : prev)), 2000);
		} catch (err) {
			console.error("Clipboard copy failed:", err);
		}
	};

	const generateCode = async () => {
		try {
			await billingRequest.generateReferralCode({
				userId: userData.user.userId,
				accessToken: userData.cognitoTokens.AccessToken,
			});
			loadReferral();
		} catch (err) {
			console.error("Failed to generate referral code:", err);
		}
	};

	if (loading) return <AccountReferralSkeleton />;

	if (error || !referral) {
		return (
			<div className="ac-page">
				<div className="ac-empty">
					<span className="ac-icon is-danger">
						<AlertCircle size={18} />
					</span>
					<p>Unable to load referral information</p>
				</div>
			</div>
		);
	}

	const referralsIssued = referral.referralsIssued || referral.referrals || [];
	const referralReceived = referral.referralReceived || null;
	const successfulIssued = referralsIssued.filter((r) => r.status === "reward_issued");
	const pendingIssued = referralsIssued.filter((r) => r.status !== "reward_issued");
	const totalSavings = successfulIssued.length * REFERRAL_VALUE;
	const referralLink = referral.referralCode ? `https://reservasinfila.com/listyourshop/${referral.referralCode}` : null;

	const steps = [t.steps?.one, t.steps?.two, t.steps?.three].filter(Boolean);

	/* Highlight the amount in place rather than reordering the sentence —
	   "Save $15 USD per referral" and "Ahorra $15 USD por referido" both
	   keep their grammar this way. */
	const [titleBefore, titleAfter] = t.hero.title.split(REFERRAL_AMOUNT);
	const hasAmount = titleAfter !== undefined;

	return (
		<div className="ac-page apr">
			{/* ── HERO ── */}
			<section className="ac-hero">
				<span className="ac-hero-icon">
					<Gift size={22} />
				</span>

				<h1 className="ac-hero-title">
					{hasAmount ? (
						<>
							{titleBefore}
							<em>$15&nbsp;USD</em>
							{titleAfter}
						</>
					) : (
						t.hero.title
					)}
				</h1>

				<p className="ac-hero-sub">{t.hero.subtitle}</p>

				{totalSavings > 0 && (
					<span className="ac-hero-pill">
						<CheckCircle size={14} />
						{t.hero.earned(totalSavings)}
					</span>
				)}
			</section>

			{/* ── AT A GLANCE ── */}
			{referralsIssued.length > 0 && (
				<div className="ac-stats ac-stats--3">
					<div className="ac-stat">
						<span className="ac-stat-value is-ok">{successfulIssued.length}</span>
						<span className="ac-stat-label">{t.stats?.active || "Active"}</span>
					</div>
					<div className="ac-stat">
						<span className="ac-stat-value">{pendingIssued.length}</span>
						<span className="ac-stat-label">{t.stats?.pending || "Pending"}</span>
					</div>
					<div className="ac-stat">
						<span className="ac-stat-value is-gold">${totalSavings}</span>
						<span className="ac-stat-label">{t.stats?.saved || "Saved"}</span>
					</div>
				</div>
			)}

			<div className="apr-grid">
				{/* ── YOUR CODE + LINK ── */}
				<section className="ac-card">
					<div className="ac-card-head">
						<span className="ac-icon is-gold">
							<Share2 size={16} />
						</span>
						<h3>{t.code.label}</h3>
					</div>

					<div className="ac-card-body">
						{referral.referralCode ? (
							<>
								<div className="ac-copy">
									<span className="ac-copy-text">{referral.referralCode}</span>
									<button
										className={`ac-copy-btn ${copied === "code" ? "is-copied" : ""}`}
										onClick={() => copyToClipboard(referral.referralCode, "code")}
									>
										{copied === "code" ? <Check size={14} /> : <Copy size={14} />}
										{t.code.copy}
									</button>
								</div>

								<p className="ac-label">{t.link.label}</p>

								<div className="ac-copy">
									<span className="ac-copy-text is-link">{referralLink}</span>
									<button
										className={`ac-copy-btn ${copied === "link" ? "is-copied" : ""}`}
										onClick={() => copyToClipboard(referralLink, "link")}
									>
										{copied === "link" ? <Check size={14} /> : <Share2 size={14} />}
										{t.link.copy}
									</button>
								</div>

								<div className="ac-note">
									<Clock size={14} />
									<span>{t.hint}</span>
								</div>
							</>
						) : (
							<div className="ac-empty">
								<span className="ac-icon is-gold">
									<Gift size={18} />
								</span>
								<p>{translations[language].billing?.referral?.noCode}</p>
								<button className="ac-btn ac-btn--sm" onClick={generateCode}>
									{translations[language].billing?.referral?.generate}
								</button>
							</div>
						)}

						<button className="ac-linkbtn apr-faq" onClick={() => navigate("/account/faq")}>
							<HelpCircle size={14} />
							{t.faqLink}
						</button>
					</div>
				</section>

				{/* ── HOW IT WORKS ── */}
				{steps.length > 0 && (
					<section className="ac-card">
						<div className="ac-card-head">
							<span className="ac-icon">
								<Users size={16} />
							</span>
							<h3>{t.steps.title}</h3>
						</div>

						<div className="ac-card-body">
							<ol className="apr-steps">
								{steps.map((step, i) => (
									<li key={i} className="apr-step">
										<span className="apr-step-num">{i + 1}</span>
										<span className="apr-step-copy">
											<span className="apr-step-title">{step.title}</span>
											<span className="apr-step-text">{step.text}</span>
										</span>
									</li>
								))}
							</ol>
						</div>
					</section>
				)}
			</div>

			{/* ── SHOPS YOU REFERRED ── */}
			{isOwner && (
				<section className="ac-card">
					<div className="ac-card-head">
						<span className="ac-icon">
							<ArrowUpRight size={16} />
						</span>
						<h3>{t.history?.title || "Shops You Referred"}</h3>
						{referralsIssued.length > 0 && (
							<span className="apr-head-stats">
								{successfulIssued.length > 0 && (
									<span className="ac-badge ac-badge--ok">
										<CheckCircle size={11} />
										{successfulIssued.length}
									</span>
								)}
								{pendingIssued.length > 0 && (
									<span className="ac-badge ac-badge--warn">
										<Clock size={11} />
										{pendingIssued.length}
									</span>
								)}
							</span>
						)}
					</div>

					{referralsIssued.length > 0 ? (
						<div className="ac-card-body is-flush">
							{referralsIssued.map((r, i) => (
								<div key={i} className="apr-row">
									<div className="apr-row-copy">
										<span className="apr-row-name">{r.invitedShopName || r.refereeEmail || "New shop"}</span>
										{r.refereeEmail && r.invitedShopName && <span className="apr-row-meta">{r.refereeEmail}</span>}
										{r.createdAt && <span className="apr-row-meta">{new Date(r.createdAt).toLocaleDateString()}</span>}
									</div>

									<div className="apr-row-right">
										<span className={`ac-badge ac-badge--${toneOf(r.status)}`}>
											{t.statusLabels?.[r.status] || t.history?.rewarded || r.status}
										</span>
										{r.rewardIssued && <span className="apr-reward">+${REFERRAL_VALUE}</span>}
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="ac-card-body">
							<div className="ac-empty">
								<span className="ac-icon">
									<Users size={18} />
								</span>
								<p>
									{t.history?.empty}
									{t.history?.emptyHint && (
										<>
											<br />
											{t.history.emptyHint}
										</>
									)}
								</p>
							</div>
						</div>
					)}
				</section>
			)}

			{/* ── REFERRAL YOU USED ── */}
			{isOwner && (
				<section className="ac-card">
					<div className="ac-card-head">
						<span className="ac-icon">
							<ArrowDownLeft size={16} />
						</span>
						<h3>{t.received?.title || "Referral You Used"}</h3>
					</div>

					<div className={`ac-card-body ${referralReceived ? "is-flush" : ""}`}>
						{referralReceived ? (
							<div className="apr-row">
								<div className="apr-row-copy">
									<span className="apr-row-name">
										{referralReceived.referrerShopName || referralReceived.referrerEmail || "Referrer"}
									</span>
									{referralReceived.referrerEmail && referralReceived.referrerShopName && (
										<span className="apr-row-meta">{referralReceived.referrerEmail}</span>
									)}
									<span className="ac-code apr-row-code">{referralReceived.code}</span>
									{referralReceived.createdAt && (
										<span className="apr-row-meta">{new Date(referralReceived.createdAt).toLocaleDateString()}</span>
									)}
								</div>

								<span className={`ac-badge ac-badge--${referralReceived.rewardIssued ? "ok" : toneOf(referralReceived.status)}`}>
									{referralReceived.rewardIssued
										? t.history?.rewarded || "Rewarded"
										: t.statusLabels?.[referralReceived.status] || referralReceived.status || "Pending"}
								</span>
							</div>
						) : (
							<div className="ac-empty">
								<span className="ac-icon">
									<ArrowDownLeft size={18} />
								</span>
								<p>{t.received?.empty || "You haven't used a referral code yet"}</p>
							</div>
						)}
					</div>
				</section>
			)}
		</div>
	);
};

export default AccountReferral;
